/**
 * useJournalContext Hook
 * 
 * Builds and sends structured context about a journal and its blocks
 * to the voice agent for contextual understanding.
 */

import { useCallback, useRef, useMemo, useEffect } from 'react';
import type { JournalBlock, BlockType, TodoItem } from '@/types/journal';
import { devLogger } from '@/lib/dev-logger';

// Types for journal context
export interface JournalContextPayload {
    contextType: 'journal';
    payload: {
        // Journal-level
        journalId: string;
        journalName: string;
        journalSummary?: string | null;
        tutorInstructions?: string | null;

        // Active block (focused/expanded)
        activeBlock?: {
            id: string;
            type: BlockType;
            title: string;
            summary?: string | null;
            tutorContext?: string | null;

            // Type-specific context
            explainerSlide?: {
                slideIndex: number;
                title: string;
                tutorPrompt?: string | null;
            };
            quizState?: {
                currentQuestion: number;
                totalQuestions: number;
                score: number;
                completed: boolean;
            };
            pdfState?: {
                currentPage?: number;
                pageCount?: number;
                fileName?: string;
            };
            youtubeState?: {
                currentTime?: number;
                title?: string;
                duration?: string;
            };
        } | null;

        // Learning Plan (Todo Context)
        learningPlan?: {
            activeGoals: string[];
            completedGoals: string[];
            nextSuggested: string | null;
        };

        // Recent Notes (for edit_note tool)
        recentNotes?: Array<{
            id: string;
            title: string;
            preview: string;
        }>;

        // Phase 8a: User Memories (profile-level, cross-session)
        userMemories?: string[];

        // Phase 8b: Session History (for continuity)
        sessionHistory?: SessionSummary[];

        // Overview
        blockCount: number;
        blockTypes: string[];
    };
}

// Phase 8b: Session summary type
export interface SessionSummary {
    id: string;
    timestamp: string;
    duration_minutes?: number;
    topics_covered?: string[];
    key_moments?: string[];
    todos_added?: string[];
    todos_completed?: string[];
    summary: string;
}

// Journal data type (from useJournal hook)
export interface Journal {
    id: string;
    name: string;
    summary?: string | null;
    tutor_instructions?: string | null;
    todo_items?: TodoItem[] | null;
    ai_context?: {
        journal_summary?: string;
        key_topics?: string[];
    } | null;
    session_history?: SessionSummary[] | null;  // Phase 8b
}

// Active block state for tracking explainer slides, quiz progress, etc.
export interface ActiveBlockState {
    blockId: string;
    explainerSlideIndex?: number;
    quizCurrentQuestion?: number;
    quizScore?: number;
    quizCompleted?: boolean;
    pdfCurrentPage?: number;
    youtubeCurrentTime?: number;
}

interface UseJournalContextOptions {
    journal: Journal | null | undefined;
    blocks: JournalBlock[] | undefined;
    activeBlockId?: string | null;
    activeBlockState?: ActiveBlockState | null;
    // The sendContextUpdate function from MultimodalLiveClient
    sendContextUpdate?: (payload: object) => void;
    isAgentConnected?: boolean;
    // Phase 8a: User memories from profile
    userMemories?: string[];
}

export function useJournalContext({
    journal,
    blocks,
    activeBlockId,
    activeBlockState,
    sendContextUpdate,
    isAgentConnected,
    userMemories,
}: UseJournalContextOptions) {
    const lastContextKeyRef = useRef<string | null>(null);
    const hasSentInitialContextRef = useRef(false);

    // Build context payload from journal and blocks
    const buildContextPayload = useCallback((): JournalContextPayload | null => {
        if (!journal) return null;

        const blockList = blocks || [];
        const blockTypes = [...new Set(blockList.map(b => b.block_type))];

        // Find active block if specified
        let activeBlockPayload: JournalContextPayload['payload']['activeBlock'] = null;

        if (activeBlockId) {
            const activeBlock = blockList.find(b => b.id === activeBlockId);
            if (activeBlock) {
                const content = activeBlock.content as Record<string, unknown> | null;

                activeBlockPayload = {
                    id: activeBlock.id,
                    type: activeBlock.block_type,
                    title: activeBlock.title,
                    summary: activeBlock.summary || null,
                    tutorContext: activeBlock.tutor_context || null,
                };

                // Add type-specific state
                if (activeBlock.block_type === 'explainer' && content) {
                    const slides = content.slides as Array<{ title?: string; tutorPrompt?: string }> | undefined;
                    const slideIndex = activeBlockState?.explainerSlideIndex ?? (content.currentSlideIndex as number | undefined) ?? 0;
                    const currentSlide = slides?.[slideIndex];

                    activeBlockPayload.explainerSlide = {
                        slideIndex,
                        title: currentSlide?.title || 'Slide ' + (slideIndex + 1),
                        tutorPrompt: currentSlide?.tutorPrompt || null,
                    };
                }

                if (activeBlock.block_type === 'quiz' && content) {
                    const questions = content.questions as unknown[] | undefined;
                    activeBlockPayload.quizState = {
                        currentQuestion: activeBlockState?.quizCurrentQuestion ?? (content.currentQuestionIndex as number | undefined) ?? 0,
                        totalQuestions: questions?.length ?? 0,
                        score: activeBlockState?.quizScore ?? (content.score as number | undefined) ?? 0,
                        completed: activeBlockState?.quizCompleted ?? (content.completed as boolean | undefined) ?? false,
                    };
                }

                if (activeBlock.block_type === 'pdf' && content) {
                    activeBlockPayload.pdfState = {
                        currentPage: activeBlockState?.pdfCurrentPage ?? (content.currentPage as number | undefined),
                        pageCount: content.pageCount as number | undefined,
                        fileName: content.fileName as string | undefined,
                    };
                }

                if (activeBlock.block_type === 'youtube' && content) {
                    activeBlockPayload.youtubeState = {
                        currentTime: activeBlockState?.youtubeCurrentTime ?? (content.currentTime as number | undefined),
                        title: content.title as string | undefined,
                        duration: content.duration as string | undefined,
                    };
                }
            }
        }

        // Extract journal summary from ai_context if available
        const journalSummary = journal.summary ||
            (journal.ai_context?.journal_summary) ||
            null;

        // Build learning plan context from todos
        const todos = journal.todo_items || [];
        const activeTodos = todos.filter(t => !t.completed).sort((a, b) => a.order - b.order);
        const completedTodos = todos.filter(t => t.completed);
        const learningPlan = todos.length > 0 ? {
            activeGoals: activeTodos.map(t => t.text),
            completedGoals: completedTodos.map(t => t.text),
            nextSuggested: activeTodos.length > 0 ? activeTodos[0].text : null,
        } : undefined;

        // Extract recent notes
        const recentNotes = blockList
            .filter(b => b.block_type === 'note')
            .slice(-5)
            .map(b => {
                let preview = '';
                const content = b.content as any;

                if (content && typeof content === 'object') {
                    if ('markdown' in content && typeof content.markdown === 'string') {
                        preview = content.markdown.substring(0, 100);
                    } else if (Array.isArray(content) && content.length > 0) {
                        // Extract text from first few blocks for preview
                        const firstBlock = content[0];
                        if (firstBlock.content && Array.isArray(firstBlock.content)) {
                            preview = firstBlock.content
                                .map((item: any) => item.text || '')
                                .join('')
                                .substring(0, 100);
                        }
                    }
                }

                return {
                    id: b.id,
                    title: b.title,
                    preview
                };
            });

        return {
            contextType: 'journal',
            payload: {
                journalId: journal.id,
                journalName: journal.name,
                journalSummary,
                tutorInstructions: journal.tutor_instructions || null,
                activeBlock: activeBlockPayload,
                learningPlan,
                recentNotes: recentNotes.length > 0 ? recentNotes : undefined,
                // Phase 8a: User memories for personalization
                userMemories: userMemories && userMemories.length > 0 ? userMemories : undefined,
                // Phase 8b: Session history for continuity (last 3 sessions)
                sessionHistory: journal.session_history && journal.session_history.length > 0
                    ? journal.session_history.slice(-3)
                    : undefined,
                blockCount: blockList.length,
                blockTypes,
            },
        };
    }, [journal, blocks, activeBlockId, activeBlockState, userMemories]);

    // Generate a key for change detection
    const contextKey = useMemo(() => {
        if (!journal) return null;
        const activeBlock = activeBlockId || 'none';
        const slideIndex = activeBlockState?.explainerSlideIndex ?? 'na';
        const quizQ = activeBlockState?.quizCurrentQuestion ?? 'na';
        return `${journal.id}:${activeBlock}:${slideIndex}:${quizQ}`;
    }, [journal, activeBlockId, activeBlockState]);

    // Send context update
    const sendJournalContext = useCallback((force = false) => {
        if (!sendContextUpdate) return;
        if (!force && contextKey === lastContextKeyRef.current) return;

        const payload = buildContextPayload();
        if (!payload) return;

        try {
            sendContextUpdate(payload);
            lastContextKeyRef.current = contextKey;
            devLogger.log('📚 Sent journal context update', payload);
        } catch (error) {
            devLogger.warn('sendJournalContext failed', error);
        }
    }, [sendContextUpdate, contextKey, buildContextPayload]);

    // Send initial context when agent connects
    useEffect(() => {
        if (isAgentConnected && !hasSentInitialContextRef.current && journal) {
            // Delay slightly to ensure backend is ready
            const timer = setTimeout(() => {
                sendJournalContext(true);
                hasSentInitialContextRef.current = true;
            }, 500);
            return () => clearTimeout(timer);
        }

        // Reset when disconnected
        if (!isAgentConnected) {
            hasSentInitialContextRef.current = false;
            lastContextKeyRef.current = null;
        }
    }, [isAgentConnected, journal, sendJournalContext]);

    // Send context when active block or state changes
    useEffect(() => {
        if (isAgentConnected && hasSentInitialContextRef.current) {
            sendJournalContext();
        }
    }, [isAgentConnected, contextKey, sendJournalContext]);

    return {
        sendJournalContext,
        buildContextPayload,
    };
}
